'use client';

import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, Divider, Drawer, Fab, IconButton, Typography } from '@mui/material';

import { BrowseFiltersList } from '@/components';
import { useT } from '@/hooks';
import { SearchFiltersList } from './SearchFiltersList';

import { BROWSE_FILTERS, BrowseFilter, EVENT_STATUS } from '@/constants';

interface MobileFilterDrawerProps {
  status: string;
  onStatusChange: (status: string) => void;
  onBrowseClick: (filter: BrowseFilter) => void;
  onClearFilters: () => void;
}

export function MobileFilterDrawer({
  status,
  onStatusChange,
  onBrowseClick,
  onClearFilters,
}: MobileFilterDrawerProps) {
  const [open, setOpen] = useState(false);
  const { t } = useT();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="filter"
        onClick={toggleDrawer(true)}
        sx={{
          position: 'fixed',
          bottom: { xs: 80, sm: 32 },
          right: { xs: 16, sm: 32 },
          display: { xs: 'flex', lg: 'none' },
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.5)'
              : '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1100,
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 6px 24px rgba(0,0,0,0.6)'
                : '0 6px 24px rgba(0,0,0,0.2)',
          },
          transition: (theme) =>
            theme.transitions.create(['all'], {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeInOut,
            }),
        }}
      >
        <FilterListIcon />
      </Fab>

      {/* Bottom Drawer */}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '85vh',
            backgroundImage: 'none',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'text.primary' }}
            >
              {t('search.searchFilters')}
            </Typography>
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{ bgcolor: 'action.hover', color: 'text.primary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ mx: -2.5 }} />

          {/* Browse Section */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                opacity: 0.8,
              }}
            >
              {t('search.browseCategories')}
            </Typography>
            <BrowseFiltersList
              title={t('search.browseCategories')}
              filters={BROWSE_FILTERS}
              onFilterClick={(filter) => {
                onBrowseClick(filter);
                setOpen(false);
              }}
            />
          </Box>

          <Divider sx={{ mx: -2.5 }} />

          {/* Status Filters */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                opacity: 0.8,
              }}
            >
              {t('search.marketStatus')}
            </Typography>
            <SearchFiltersList
              filters={EVENT_STATUS}
              onFilterClick={(newStatus) => {
                onStatusChange(newStatus);
                setOpen(false);
              }}
              activeValue={status}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 1, pb: 1, display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={() => {
                onClearFilters();
                setOpen(false);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                fontWeight: 600,
                borderColor: 'divider',
                color: 'text.primary',
                py: 1.25,
                '&:hover': {
                  borderColor: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {t('search.clearAll')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={toggleDrawer(false)}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                fontWeight: 700,
                boxShadow: 'none',
                py: 1.25,
                '&:hover': {
                  boxShadow: (theme) => theme.shadows[4],
                },
              }}
            >
              {t('search.apply')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
